import React, { useEffect, useState, useCallback } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Gauge } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { WalletConnectCard } from '../../components/pas/WalletConnectCard';
import { SubscriptionCard, CreditLineCard } from '../../components/pas/OfficerCards';
import { ValuationSummaryCard } from '../../components/pas/ValuationSummaryCard';
import { TreasuryCheckCard } from '../../components/pas/TreasuryCheckCard';
import { BuyEthCard } from '../../components/pas/BuyEthCard';
import { BuyGasCard } from '../../components/pas/BuyGasCard';
import { Web3AuthCard } from '../../components/pas/Web3AuthCard';
import { TreasuryCheckFeeCard } from '../../components/pas/TreasuryCheckFeeCard';
import { DeployAgreementCard } from '../../components/pas/DeployAgreementCard';
import { NonFdicNotice } from '../../components/NonFdicNotice';
import { CollateralDocsReminder } from '../../components/pas/CollateralDocsReminder';
import { SignaturePad } from '../../components/pas/SignaturePad';
import { OtherPaymentOptions } from '../../components/pas/OtherPaymentOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DailyLimit = ({ used, limit }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid="daily-limit-card">
    <p className="text-slate-400 text-sm flex items-center gap-2"><Gauge className="w-4 h-4" /> Daily transaction limit</p>
    {limit === null ? (
      <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="daily-limit-used">No daily limit <span className="text-base text-slate-500">· ${used.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} moved today</span></p>
    ) : (<>
    <p className="text-2xl font-bold text-white mt-1" data-testid="daily-limit-used">${used.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-base text-slate-500">of ${limit.toLocaleString()} used today</span></p>
    <div className="h-2 bg-slate-900 rounded-full mt-3 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min((used / limit) * 100, 100)}%` }} /></div>
    </>)}
  </div>
);

export default function PASBanking() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pasClient, token, loading } = usePASClientAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/pas-banking/overview`, { headers: { Authorization: `Bearer ${token}` } });
    setData(res.data);
  }, [token]);

  // Handle fee payment confirmation from Stripe redirect
  useEffect(() => {
    const confirmFeePayment = async (feeId) => {
      try {
        const res = await axios.post(`${API}/pas-treasury-check/fee/confirm/${feeId}`, {}, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(res.data.message || 'Fee payment confirmed!', { duration: 10000 });
      } catch (err) {
        if (err.response?.status !== 404) {
          const d = err.response?.data?.detail;
          toast.error(typeof d === 'string' ? d : 'Could not confirm the fee payment yet. Please refresh in a minute.');
        }
      }
      // Clear the URL parameter
      searchParams.delete('fee_paid');
      setSearchParams(searchParams, { replace: true });
    };

    const confirmDeployPayment = async (deployId) => {
      try {
        const res = await axios.post(`${API}/pas-banking/credit-line/agreement/deploy/confirm?deploy_id=${deployId}`, {}, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.status === 'deployed') {
          toast.success('Agreement deployed to Ethereum!');
        } else {
          toast.info('Deployment in progress...');
        }
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Deployment confirmation failed');
      }
      searchParams.delete('deploy_paid');
      setSearchParams(searchParams, { replace: true });
    };

    const feeId = searchParams.get('fee_paid');
    if (feeId && token) {
      confirmFeePayment(feeId);
    }

    const deployId = searchParams.get('deploy_paid');
    if (deployId && token) {
      confirmDeployPayment(deployId);
    }
    
    // Handle cancelled fee
    const cancelledFeeId = searchParams.get('fee_cancelled');
    if (cancelledFeeId) {
      toast.error('Fee payment was cancelled');
      searchParams.delete('fee_cancelled');
      setSearchParams(searchParams, { replace: true });
    }

    const cancelledDeployId = searchParams.get('deploy_cancelled');
    if (cancelledDeployId) {
      toast.error('Deployment payment was cancelled');
      searchParams.delete('deploy_cancelled');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, token, load]);

  useEffect(() => { if (!loading && !pasClient) navigate('/pas/login'); }, [loading, pasClient, navigate]);
  useEffect(() => { if (token) load(); }, [token, load]);

  if (!data) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/pas/dashboard')} className="p-2 text-slate-400 hover:text-white" data-testid="banking-back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-lg font-bold text-white">Banking &amp; Credit</h1>
            <p className="text-xs text-slate-400">{data.rate.display} &middot; Balance {data.beth_balance.toLocaleString()} BETH</p>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <NonFdicNotice />
        <SectionGuide id="banking" />
        <CollateralDocsReminder pending={data.collateral_docs_pending} token={token} onUploaded={load} />
        <div className="grid lg:grid-cols-2 gap-6">
          <WalletConnectCard data={data} token={token} onChange={load} />
          <div className="space-y-6">
            <SubscriptionCard data={data} token={token} onChange={load} />
            <DailyLimit used={data.used_today_usd} limit={data.daily_limit_usd} />
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <TreasuryCheckFeeCard token={token} onFeePaid={load} exempt={data.fee_exempt} />
          <OtherPaymentOptions>
            <Web3AuthCard token={token} onWalletLinked={load} linked={data.linked_wallet} />
            <BuyGasCard token={token} walletAddress={data.wallet_address} />
            <BuyEthCard data={data} token={token} onChange={load} />
          </OtherPaymentOptions>
        </div>
        <CreditLineCard data={data} token={token} onChange={load} />
        <DeployAgreementCard token={token} agreement={data.credit_line?.agreement} onDeployed={load} />
        <ValuationSummaryCard token={token} />
        <TreasuryCheckCard token={token} />
        <SignaturePad token={token} />
      </main>
    </div>
  );
}
